<template>
    <Teleport to="body">
        <div class="helpModalBackdrop" @click.self="$emit('close')">
            <div class="helpModal" role="dialog" aria-modal="true">
                <div class="helpModalHeader">
                    <button
                        v-if="selectedTopic"
                        type="button"
                        class="helpModalBack"
                        @click="selectedTopic = null"
                    >
                        <i class="ti ti-arrow-left"></i> {{ $t('help.back') }}
                    </button>
                    <div class="helpModalTitle">
                        {{ headerTitle }}
                    </div>
                    <button
                        type="button"
                        class="helpModalClose"
                        :aria-label="$t('help.closeAria')"
                        @click="$emit('close')"
                    >
                        <i class="ti ti-x"></i>
                    </button>
                </div>

                <div class="helpModalBody">
                    <!-- Vista lista de topics: Documentación (artículos) y Tutoriales (tours) -->
                    <div v-if="!selectedTopic">
                        <div v-if="hayAmbas" class="helpSecciones">
                            <button type="button" class="helpSecciones__tab" :class="{'helpSecciones__tab--activa': seccionLista === 'documentacion'}" @click="seccionLista = 'documentacion'">
                                <i class="ti ti-book-2"></i> {{ $t('help.secciones.documentacion') }}
                            </button>
                            <button type="button" class="helpSecciones__tab" :class="{'helpSecciones__tab--activa': seccionLista === 'tutoriales'}" @click="seccionLista = 'tutoriales'">
                                <i class="ti ti-route"></i> {{ $t('help.secciones.tutoriales') }}
                            </button>
                        </div>
                        <input v-if="topics.length > 6" v-model="busqueda" type="search" class="form-control form-control-sm helpBuscar" :placeholder="$t('help.buscar')">

                        <p v-if="topicsVisibles.length === 0" class="helpModalEmpty">
                            {{ busqueda ? $t('help.sinResultados') : $t('help.emptyState') }}
                        </p>
                        <ul v-else class="helpTopicList">
                            <li
                                v-for="topic in topicsVisibles"
                                :key="topic.id"
                                class="helpTopicItem"
                                tabindex="0"
                                @click="onTopicClick(topic)"
                                @keyup.enter="onTopicClick(topic)"
                            >
                                <i class="ti helpTopicIcon" :class="iconForType(topic.type)"></i>
                                <div class="helpTopicText">
                                    <div class="helpTopicTitle">{{ $t(topic.titleKey) }}</div>
                                    <div class="helpTopicMeta">{{ $t('help.types.' + topic.type) }}</div>
                                </div>
                                <i class="ti ti-chevron-right helpTopicChevron"></i>
                            </li>
                        </ul>
                    </div>

                    <!-- Vista artículo -->
                    <div v-else>
                        <HelpArticleViewer
                            v-if="selectedTopic.type === 'article'"
                            :path="selectedTopic.article"
                            :seccion="seccionArticulo"
                        />
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script>
import {getTopics} from './registry'
import HelpArticleViewer from './HelpArticleViewer.vue'

export default {
    name: 'HelpModal',
    components: {HelpArticleViewer},
    props: {
        context: {type: String, required: true},
        // abrir directo en un artículo (id del topic) y, dentro de él, en una HelpSeccion
        initialTopic: {type: String, default: ''},
        initialSeccion: {type: String, default: ''},
    },
    emits: ['close'],
    data() {
        return {
            selectedTopic: null,
            seccionArticulo: '',
            seccionLista: 'documentacion',
            busqueda: '',
        }
    },
    computed: {
        topics() {
            return getTopics(this.context)
        },
        hayAmbas() {
            return this.topics.some(t => t.type === 'tour') && this.topics.some(t => t.type !== 'tour')
        },
        topicsVisibles() {
            const texto = this.normalizar(this.busqueda)
            return this.topics.filter(t => {
                if (this.hayAmbas && !texto) {
                    const esTour = t.type === 'tour'
                    if ((this.seccionLista === 'tutoriales') !== esTour) return false
                }
                return !texto || this.normalizar(this.$t(t.titleKey)).includes(texto)
            })
        },
        headerTitle() {
            if (this.selectedTopic) {
                return this.$t(this.selectedTopic.titleKey)
            }
            return this.$t('help.modalTitle')
        },
    },
    mounted() {
        document.addEventListener('keydown', this.onKeydown)
        document.body.style.overflow = 'hidden'
        if (this.initialTopic) {
            const topic = this.topics.find(t => t.id === this.initialTopic)
            if (topic && topic.type !== 'tour') {
                this.seccionArticulo = this.initialSeccion
                this.selectedTopic = topic
            }
        }
    },
    beforeUnmount() {
        document.removeEventListener('keydown', this.onKeydown)
        document.body.style.overflow = ''
    },
    methods: {
        // búsqueda sin distinguir mayúsculas ni tildes
        normalizar(t) {
            return String(t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
        },
        onTopicClick(topic) {
            this.seccionArticulo = ''
            if (topic.type === 'tour') {
                this.launchTour(topic)
            } else {
                this.selectedTopic = topic
            }
        },
        async launchTour(topic) {
            // Cierra el modal antes de arrancar el tour para que el highlight
            // tenga la pantalla limpia y los selectores no queden tapados.
            this.$emit('close')
            try {
                const [tourModule, runner] = await Promise.all([
                    import(/* webpackChunkName: "help-tour-[request]" */ `./tours/${topic.tour}.js`),
                    import('./tours/runner.js'),
                ])
                runner.startTour(tourModule.default)
            } catch (e) {
                console.error('[Help] no se pudo cargar el tour', topic.tour, e)
            }
        },
        iconForType(type) {
            if (type === 'tour') return 'ti-route'
            return 'ti-book-2'
        },
        onKeydown(e) {
            if (e.key === 'Escape') {
                if (this.selectedTopic) {
                    this.selectedTopic = null
                } else {
                    this.$emit('close')
                }
            }
        },
    },
}
</script>

<style scoped>
.helpSecciones {
    display: flex;
    gap: 0.25rem;
    padding: 0.25rem;
    border-radius: 10px;
    background: var(--cui-gray-100, #f6f6f7);
    margin-bottom: 0.9rem;
}

.helpSecciones__tab {
    flex: 1 1 0;
    border: 0;
    background: transparent;
    border-radius: 8px;
    padding: 0.5rem 0.75rem;
    font-size: 0.88rem;
    color: var(--cui-secondary-color, #6e7278);
}

.helpSecciones__tab--activa {
    background: #fff;
    color: var(--cui-body-color, #2c2e32);
    font-weight: 600;
    box-shadow: 0 1px 3px rgba(16, 24, 40, 0.12);
}

.helpBuscar {
    margin-bottom: 0.9rem;
}

.helpModalBackdrop {
    position: fixed;
    inset: 0;
    z-index: 999999999;
    background: rgba(15, 23, 42, 0.55);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
}

.helpModal {
    background: #ffffff;
    width: 100%;
    max-width: 900px;
    max-height: 90vh;
    border-radius: 12px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.4);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.helpModalHeader {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.85rem 1.25rem;
    border-bottom: 1px solid var(--cui-gray-200, #e4e6ea);
    background: var(--cui-gray-50, #f9f9fa);
}

.helpModalBack {
    display: inline-flex;
    align-items: center;
    gap: 0.3em;
    border: none;
    background: transparent;
    color: var(--cui-primary);
    cursor: pointer;
    font-size: 0.9em;
    padding: 0.25em 0.5em;
    border-radius: 6px;
    transition: background 0.15s ease;
}

.helpModalBack:hover {
    background: rgba(13, 110, 253, 0.08);
}

.helpModalTitle {
    flex: 1;
    font-weight: 700;
    font-size: 1.05em;
    color: var(--cui-gray-900, #2C2E32);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.helpModalClose {
    border: none;
    background: transparent;
    color: var(--cui-gray-600, #6e7278);
    cursor: pointer;
    font-size: 1.5em;
    line-height: 1;
    padding: 0.15em 0.35em;
    border-radius: 6px;
    transition: background 0.15s ease, color 0.15s ease;
}

.helpModalClose:hover {
    background: var(--cui-gray-200, #e4e6ea);
    color: var(--cui-gray-900, #2C2E32);
}

.helpModalBody {
    padding: 1.25rem 1.5rem 1.5rem;
    overflow-y: auto;
    flex: 1;
}

.helpModalEmpty {
    text-align: center;
    color: var(--cui-gray-600, #6e7278);
    padding: 2rem 1rem;
    font-size: 0.95em;
}

.helpTopicList {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.helpTopicItem {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    padding: 0.85rem 1rem;
    background: var(--cui-gray-50, #f9f9fa);
    border-radius: 10px;
    cursor: pointer;
    transition: background 0.15s ease, transform 0.15s ease;
}

.helpTopicItem:hover,
.helpTopicItem:focus-visible {
    background: rgba(13, 110, 253, 0.08);
    outline: none;
    transform: translateX(2px);
}

.helpTopicIcon {
    font-size: 1.4em;
    color: var(--cui-primary);
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(13, 110, 253, 0.12);
    border-radius: 8px;
}

.helpTopicText {
    flex: 1;
    min-width: 0;
}

.helpTopicTitle {
    font-weight: 500;
    color: var(--cui-gray-900, #2C2E32);
}

.helpTopicMeta {
    font-size: 0.78em;
    color: var(--cui-gray-600, #6e7278);
    margin-top: 0.1em;
}

.helpTopicChevron {
    color: var(--cui-gray-500, #a4a8b0);
    flex-shrink: 0;
}

@media (max-width: 576px) {
    .helpModalBackdrop {
        padding: 0;
    }

    .helpModal {
        max-width: 100%;
        max-height: 100vh;
        height: 100vh;
        border-radius: 0;
    }
}
</style>
